/**
 * לקוח ה-API של פאנל הניהול — TASKS 3.1.
 *
 * ⭐ `credentials: 'same-origin'` — מספיק כי הפאנל והשרת תמיד על אותו origin
 *    (בפיתוח דרך פרוקסי Vite, בפרודקשן אותו דומיין ממש). ⛔ אין CORS חוצה-דומיין.
 */

export interface AdminApiError {
  status: number;
  code: string;
  message: string;
}

function isAdminApiError(value: unknown): value is AdminApiError {
  return typeof value === 'object' && value !== null && 'code' in value && 'message' in value;
}

async function parseErrorBody(res: Response): Promise<AdminApiError> {
  try {
    const body = (await res.json()) as { error?: { code?: string; message?: string } };
    return {
      status: res.status,
      code: body.error?.code ?? 'UNKNOWN_ERROR',
      message: body.error?.message ?? 'אירעה שגיאה לא צפויה.',
    };
  } catch {
    return { status: res.status, code: 'UNKNOWN_ERROR', message: 'אירעה שגיאה לא צפויה.' };
  }
}

export async function adminLogin(
  username: string,
  password: string,
): Promise<{ username: string }> {
  const res = await fetch('/api/admin/login', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });

  if (!res.ok) throw await parseErrorBody(res);
  return (await res.json()) as { username: string };
}

export async function adminLogout(): Promise<void> {
  await fetch('/api/admin/logout', { method: 'POST', credentials: 'same-origin' });
}

/** @returns `null` אם אין סשן תקין — ⛔ אינו זורק על 401, זה מצב תקין (טרם התחבר) */
export async function adminMe(): Promise<{ username: string; demoPrices: boolean } | null> {
  const res = await fetch('/api/admin/me', { credentials: 'same-origin' });
  if (res.status === 401) return null;
  if (!res.ok) throw await parseErrorBody(res);
  return (await res.json()) as { username: string; demoPrices: boolean };
}

export { isAdminApiError };
